import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(() => localStorage.getItem("active_org_id") || null);

  const refreshOrgs = useCallback(async () => {
    try {
      const { data } = await api.get("/organizations");
      setOrgs(data);
      if (data.length > 0) {
        const saved = localStorage.getItem("active_org_id");
        if (!saved || !data.find((o) => o.id === saved)) {
          setActiveOrgId(data[0].id);
          localStorage.setItem("active_org_id", data[0].id);
        } else {
          setActiveOrgId(saved);
        }
      } else {
        setActiveOrgId(null);
        localStorage.removeItem("active_org_id");
      }
      return data;
    } catch (e) {
      return [];
    }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await refreshOrgs();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [refreshOrgs]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ledger_token", data.token);
    setUser(data.user);
    await refreshOrgs();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("ledger_token", data.token);
    setUser(data.user);
    await refreshOrgs();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("ledger_token");
    localStorage.removeItem("active_org_id");
    setUser(false);
    setOrgs([]);
    setActiveOrgId(null);
  };

  const switchOrg = (orgId) => {
    setActiveOrgId(orgId);
    localStorage.setItem("active_org_id", orgId);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, orgs, activeOrgId, login, register, logout, switchOrg, refreshOrgs, formatApiError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
